import { f7 } from 'framework7-react';
import { useCallback, useMemo, useState } from 'react';
import { RepairDetailDataType } from '../../../types/bodyAndPaint';
import { usePostCancelRepairCheckInReserve } from './hook/api/usePostCancelRepairCheckInReserve';
import { usePostChangeRepairCheckInDate } from './hook/api/usePostChangeRepairCheckInDate';
import { usePostUpdateInsuranceInfo } from './hook/api/usePostUpdateInsuranceInfo';
import { useCalendarItemState } from './hook/state/useCalendarItemState';
import { useInsuranceState } from './hook/state/useInsuranceState';
import { usePageTitle } from './hook/usePageTitle';
import { useRepairDetailData } from './hook/useRepairDetailData';
import { useStorageItem } from './hook/useStorageItem';
import { AddInsuranceInfoField } from './insuranceEntry/addInsuranceInfoField';
import { CancelReserveDialogCard } from './repairInfoDetail/cancelReserveDialogCard';
import { ChangeCheckInDateField } from './repairInfoDetail/changeCheckInDateField';
import { ChangeCheckInDialogCard } from './repairInfoDetail/changeCheckInDialogCard';
import { RepairDetailInformation } from './repairInfoDetail/repairDetailInformation';
import { PageTemplateComponent } from './util/common/pageTemplate/pageTemplate';
import { DialogComponent } from './util/dialogFrame';
import { ModalSheet } from './util/modalSheet';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { useToast } from '@/hooks/useToast';

export const RepairInfoDetailPage = () => {
  const idInfo = useMemo(() => {
    const { authInfo, customerInfo, reservation } = store.state;
    return {
      affiliateID: customerInfo?.m_unique_customer_id ?? 0,
      customerID: authInfo?.m_customer_id ?? 0,
      reservationID: reservation?.reservation_id ?? 0,
      stockCarID: reservation?.t_stock_car_id ?? 0,
      unitID: customerInfo?.m_unit_id ?? 0,
    };
  }, []);

  const { repairDetailData, initialInsuranceState, onReload } =
    useRepairDetailData(idInfo);

  const status = useMemo(
    () => repairDetailData?.state_info?.status ?? -1,
    [repairDetailData],
  );
  const { title } = usePageTitle({ status });

  const affiliateID = useMemo(
    () => repairDetailData?.ids.affiliateID ?? 0,
    [repairDetailData],
  );
  const customerID = useMemo(
    () => repairDetailData?.ids.customerID ?? 0,
    [repairDetailData],
  );
  const plantID = useMemo(
    () => repairDetailData?.ids.plantID ?? 0,
    [repairDetailData],
  );
  const inspectionID = useMemo(
    () => repairDetailData?.ids?.inspectionID ?? 0,
    [repairDetailData],
  );
  const bodyPaintID = useMemo(
    () => repairDetailData?.ids?.bodyPaintID ?? 0,
    [repairDetailData],
  );
  const checkInDate = useMemo(
    () => repairDetailData?.reserve_info?.check_in_date ?? ({} as Date),
    [repairDetailData],
  );

  const { postRequest: cancelReserve } = usePostCancelRepairCheckInReserve({
    m_customer_id: idInfo.customerID,
    t_pm_car_inspection_id: inspectionID,
  });

  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openChangeCheckInDialog, setOpenChangeCheckInDialog] = useState(false);
  const [openInsuranceModal, setOpenInsuranceModal] = useState(false);
  const [openCalendarModal, setOpenCalendarModal] = useState(false);

  const handleClickOpenInsuranceModal = useCallback(
    () => setOpenInsuranceModal(true),
    [],
  );
  const handleClickCloseInsuranceModal = useCallback(
    () => setOpenInsuranceModal(false),
    [],
  );
  const handleClickChangeCheckInReserve = useCallback(
    () => setOpenCalendarModal(true),
    [],
  );
  const handleOpenChangeCheckInDialog = useCallback(
    () => setOpenChangeCheckInDialog(true),
    [],
  );
  const handleClickCancelCheckInReserve = useCallback(
    () => setOpenCancelDialog(true),
    [],
  );

  const { setStorageItem: setRepairImageCheck } = useStorageItem<{
    inspectionID: number;
  }>({
    key: 'repairCheckRepairImage',
  });
  const { setStorageItem: setRepairPickUpReserve } = useStorageItem<{
    carName: string;
    affiliateID: number;
    plantID: number;
    customerID: number;
    inspectionID: number;
    bodyPaintID: number;
  }>({
    key: 'repairPickUpReserve',
  });

  const handleClickBack = () => {
    f7.views.main.router.navigate(paths.maintenance);
  };
  const handleClickCheckRepairImage = () => {
    setRepairImageCheck((prev) => ({ ...prev, inspectionID }));
    f7.views.main.router.navigate(paths.bpsRepairImageCheck);
  };
  const handleClickPickUpReserve = () => {
    setRepairPickUpReserve({
      carName: repairDetailData?.reserve_info.car_info.car_name ?? '',
      plantID,
      affiliateID,
      bodyPaintID,
      customerID,
      inspectionID,
    });
    f7.views.main.router.navigate(paths.bpsRepairPickUpReserve);
  };

  const { openToast } = useToast();

  const handleClickCheckInCancelConfirm = () => {
    cancelReserve().then(() => {
      openToast('入庫予約がキャンセルされました。');
      f7.views.main.router.navigate(paths.bpsRepairInfoDetail);
    });
  };

  const { dateTimeItem, calendarItem, dateTimeCheck } = useCalendarItemState({
    initialDate: checkInDate,
    plantID,
  });
  const { postRequest: changeCheckInDate } = usePostChangeRepairCheckInDate({
    info: {
      m_customer_id: customerID,
      t_pm_car_inspections_id: inspectionID,
      check_in_date: dateTimeItem,
    },
  });
  const calendarInfo = useMemo(() => {
    return {
      header: calendarItem?.headerItem,
      date: calendarItem?.dateList,
      time: calendarItem?.timeList,
    };
  }, [calendarItem]);
  const handleClickChangeCheckIn = useCallback(() => {
    changeCheckInDate().then(() => handleOpenChangeCheckInDialog());
  }, [changeCheckInDate, handleOpenChangeCheckInDialog]);

  const {
    insuranceState,
    insuranceSetState,
    insuranceSelectList,
    insuranceError,
    pickerInfo,
    isInsurance,
    hasDeductible,
    disabled,
    postParameters,
  } = useInsuranceState({
    ids: {
      customerID: idInfo.customerID,
      inspectionID: inspectionID,
      affiliateID: idInfo.affiliateID,
    },
    initial: initialInsuranceState,
  });

  const { postRequest: updateInsurance } = usePostUpdateInsuranceInfo({
    params: postParameters,
  });

  const handleClickAddInsurance = useCallback(() => {
    updateInsurance().then(() => {
      onReload();
      handleClickCloseInsuranceModal();
      f7.views.main.router.navigate(paths.bpsRepairInfoDetail);
    });
  }, [updateInsurance, onReload, handleClickCloseInsuranceModal]);

  return (
    <PageTemplateComponent
      pageName={'checkInReserve'}
      title={title}
      onBack={handleClickBack}
    >
      <RepairDetailInformation
        data={repairDetailData ?? ({} as RepairDetailDataType)}
        openInsuranceModal={handleClickOpenInsuranceModal}
        changeCheckInReserve={handleClickChangeCheckInReserve}
        cancelCheckInReserve={handleClickCancelCheckInReserve}
        onCheckRepairImage={handleClickCheckRepairImage}
        onPickUpReserve={handleClickPickUpReserve}
        bp_status={status}
      />
      <DialogComponent open={openCancelDialog}>
        <CancelReserveDialogCard
          onClose={() => setOpenCancelDialog(false)}
          onConfirm={handleClickCheckInCancelConfirm}
        />
      </DialogComponent>
      <DialogComponent open={openChangeCheckInDialog}>
        <ChangeCheckInDialogCard
          onClick={() => {
            openToast('入庫予約が変更されました。');
            f7.views.main.router.navigate(paths.bpsRepairInfoDetail);
            setOpenChangeCheckInDialog(false);
          }}
        />
      </DialogComponent>
      <ModalSheet
        title={'保険の登録'}
        open={openInsuranceModal}
        onClose={() => setOpenInsuranceModal(false)}
      >
        <AddInsuranceInfoField
          cancelEntry={handleClickCloseInsuranceModal}
          state={insuranceState}
          setState={insuranceSetState}
          selectList={insuranceSelectList}
          error={insuranceError}
          addInsurance={false}
          hasDeductible={hasDeductible}
          isInsurance={isInsurance}
          disabled={disabled}
          onRegisterInsurance={handleClickAddInsurance}
          pickerInfo={pickerInfo}
        />
      </ModalSheet>
      <ModalSheet
        title={'日程の変更'}
        open={openCalendarModal}
        onClose={() => setOpenCalendarModal(false)}
      >
        <ChangeCheckInDateField
          calendarInfo={calendarInfo}
          disabled={dateTimeCheck}
          onClickChangeCheckIn={handleClickChangeCheckIn}
        />
      </ModalSheet>
    </PageTemplateComponent>
  );
};
